/**
 * Tech Stack Documentation
 * Complete technology stack reference for the Engraveca platform
 */

export const techStackDoc = {
  title: 'Tech Stack',
  content: `# Engraveca Platform — Tech Stack

## Architecture Overview

\`\`\`mermaid
flowchart TB
    subgraph Client["Frontend — React 19"]
        direction TB
        UI[Shadcn UI + Tailwind CSS]
        Router[React Router v7]
        State[Axios HTTP Client]
    end

    subgraph Server["Backend — FastAPI"]
        direction TB
        API[REST API — 40+ Route Modules]
        Services[Business Logic Services]
        Models[Pydantic Data Models]
    end

    subgraph Data["Database — MongoDB"]
        direction TB
        Motor[Motor Async Driver]
        Collections[20+ Collections]
    end

    Client -->|"HTTPS /api/*"| Server
    Server -->|"Motor (async)"| Data
\`\`\`

---

## Frontend

| Layer | Technology | Version | Purpose |
|-------|-----------|---------|---------|
| **Framework** | React | 19.x | Component-based UI |
| **Routing** | React Router DOM | 7.x | Client-side routing |
| **Styling** | Tailwind CSS | 3.x | Utility-first CSS |
| **Components** | Shadcn UI (Radix) | latest | Accessible component primitives |
| **HTTP** | Axios | 1.8.x | API communication |
| **Forms** | React Hook Form + Zod | 7.x / 3.x | Form handling + validation |
| **Build** | Create React App (CRACO) | 5.x | Build toolchain |
| **Icons** | Lucide React | 0.507 | Icon library |

### Key Frontend Libraries

| Library | Purpose |
|---------|---------|
| \`react-markdown\` + \`remark-gfm\` | In-app documentation rendering |
| \`mermaid\` | Interactive flowchart diagrams |
| \`html2pdf.js\` | Client-side PDF export |
| \`xlsx\` | Excel/CSV file processing (bulk import) |
| \`date-fns\` | Date formatting and manipulation |
| \`sonner\` | Toast notifications |
| \`embla-carousel-react\` | Hero slider carousel |
| \`cmdk\` | Command palette / search |
| \`vaul\` | Drawer component |
| \`lodash.debounce\` | Input debouncing |

### Shadcn UI Components Used

Accordion, Alert Dialog, Avatar, Checkbox, Collapsible, Context Menu, Dialog, Dropdown Menu, Hover Card, Label, Menubar, Navigation Menu, Popover, Progress, Radio Group, Scroll Area, Select, Separator, Slider, Switch, Tabs, Toast, Toggle, Tooltip, and more.

---

## Backend

| Layer | Technology | Version | Purpose |
|-------|-----------|---------|---------|
| **Framework** | FastAPI | 0.110.x | Async REST API |
| **Server** | Uvicorn | 0.25.x | ASGI server |
| **Database Driver** | Motor | 3.3.x | Async MongoDB driver |
| **Validation** | Pydantic | 2.12.x | Request/response models |
| **Auth** | PyJWT + bcrypt + python-jose | latest | JWT tokens, password hashing |
| **MFA** | pyotp + qrcode | latest | TOTP-based two-factor auth |
| **Email** | Resend | 2.19.x | Transactional email delivery |
| **PDF** | ReportLab + PyMuPDF + CairoSVG | latest | PDF generation (invoices, quotes) |
| **Scraping** | BeautifulSoup4 + Playwright | latest | Vendor catalog sync |
| **Excel** | openpyxl + pandas | latest | Spreadsheet processing |

### Backend Route Modules (40+)

\`\`\`mermaid
flowchart LR
    subgraph OrderMgmt["Order Management"]
        orders[orders]
        order_lifecycle[order_lifecycle]
        invoices[invoices]
        print_documents[print_documents]
        shipping[shipping]
    end

    subgraph CRM["CRM"]
        crm[crm]
        contact[contact]
    end

    subgraph Catalog["Product Catalog"]
        product_templates[product_templates]
        option_sets[option_sets]
        products_v2[products_v2]
        pricing_calculator[pricing_calculator]
        bulk_import[bulk_import]
        image_upload[image_upload]
    end

    subgraph Finance["Finance"]
        payments[payments]
        purchase_orders[purchase_orders]
        vendor_bills[vendor_bills]
        sales_tax[sales_tax]
        sales_tax_reports[sales_tax_reports]
        checks[checks]
        bank_accounts[bank_accounts]
    end

    subgraph Admin["Administration"]
        auth[auth]
        user_management[user_management]
        mfa[mfa]
        role_audit[role_audit]
        settings[settings]
        site_settings[site_settings]
        email_templates[email_templates]
        activity_logs[activity_logs]
    end

    subgraph Vendor["Vendor Management"]
        vendors[vendors]
        vendor_sync[vendor_sync]
        vendor_credentials[vendor_credentials]
        catalog_sync[catalog_sync]
        catalog_pricing[catalog_pricing]
    end
\`\`\`

#### Complete Route List

| Module | Prefix | Description |
|--------|--------|-------------|
| \`auth\` | /api/auth | Login, registration, session management |
| \`user_management\` | /api/users | Admin user CRUD, role assignment |
| \`mfa\` | /api/mfa | Multi-factor authentication setup |
| \`role_audit\` | /api/role-audit | Permission auditing |
| \`orders\` | /api/orders | Order CRUD and management |
| \`order_lifecycle\` | /api/order-lifecycle | Quotes, invoices, work orders on orders |
| \`invoices\` | /api/invoices | AR cockpit, invoice management |
| \`print_documents\` | /api/print | Order summary, packing slip, work order prints |
| \`crm\` | /api/crm | Customer relationship management |
| \`contact\` | /api/contact | Contact form submissions |
| \`operations\` | /api/operations | Work order operations |
| \`product_templates\` | /api/templates | V2 product template CRUD |
| \`option_sets\` | /api/option-sets | Reusable option set CRUD |
| \`products_v2\` | /api/v2/products | V2 product CRUD |
| \`pricing_calculator\` | /api/pricing/v2 | Price calculation engine |
| \`products\` | /api/products | Legacy product management |
| \`unified_products\` | /api/unified-products | Unified product API |
| \`unified_categories\` | /api/unified-categories | Category management |
| \`product_types\` | /api/product-types | Product type definitions |
| \`product_images\` | /api/product-images | Product image management |
| \`image_upload\` | /api/image-upload | Drag-and-drop image uploads |
| \`bulk_import\` | /api/bulk-import | CSV/JSON product import wizard |
| \`catalog_import\` | /api/catalog-import | Catalog data import |
| \`catalog_management\` | /api/catalog-management | Catalog phase 1 foundation |
| \`catalog_sync\` | /api/catalog-sync | Catalog sync (phase 4) |
| \`catalog_pricing\` | /api/catalog-pricing | Catalog pricing (phase 5) |
| \`payments\` | /api/payments | Payment recording and tracking |
| \`purchase_orders\` | /api/purchase-orders | Purchase order management |
| \`vendor_bills\` | /api/vendor-bills | Vendor bill management |
| \`sales_tax\` | /api/sales-tax | Tax zone configuration |
| \`sales_tax_reports\` | /api/sales-tax-reports | CA CDTFA compliance reports |
| \`checks\` | /api/checks | Check printing system |
| \`bank_accounts\` | /api/bank-accounts | Bank account management |
| \`vendors\` | /api/vendors | Vendor CRUD |
| \`vendor_sync\` | /api/vendor-sync | Vendor catalog sync |
| \`vendor_credentials\` | /api/vendor-credentials | Vendor API credentials |
| \`vendor_import\` | /api/vendor-import | Legacy vendor import |
| \`shipping\` | /api/shipping | Shipping management |
| \`promotions\` | /api/promotions | Promotional pricing |
| \`hero_slider\` | /api/hero-slider | Homepage hero carousel |
| \`manufacturers\` | /api/manufacturers | Manufacturer management |
| \`pricing\` | /api/pricing | Legacy pricing |
| \`settings\` | /api/settings | Company settings |
| \`site_settings\` | /api/site-settings | Site-wide configuration |
| \`email_templates\` | /api/email-templates | Email template management |
| \`reminder_profiles\` | /api/reminder-profiles | Payment reminder automation |
| \`activity_logs\` | /api/activity-logs | Audit trail logging |
| \`uploads\` | /api/uploads | File upload management |
| \`admin\` | /api/admin | Admin dashboard utilities |

---

## Database — MongoDB

| Aspect | Detail |
|--------|--------|
| **Engine** | MongoDB (local instance) |
| **Driver** | Motor 3.3 (async) + PyMongo 4.5 |
| **Database Name** | \`engraveca\` |
| **ODM** | Pydantic models (no Mongoose/ODM layer) |

### Core Collections

\`\`\`mermaid
flowchart TB
    subgraph OrderData["Order Domain"]
        orders[(orders)]
        work_orders[(work_orders)]
        invoices_c[(invoices)]
    end

    subgraph ProductData["Product Domain"]
        product_templates_c[(product_templates)]
        option_sets_c[(option_sets)]
        products_v2_c[(products_v2)]
        products_c[(products — legacy)]
    end

    subgraph CRMData["CRM Domain"]
        customers[(customers)]
        contacts[(contacts)]
    end

    subgraph FinanceData["Finance Domain"]
        payments_c[(payments)]
        purchase_orders_c[(purchase_orders)]
        vendor_bills_c[(vendor_bills)]
        bank_accounts_c[(bank_accounts)]
        checks_c[(checks)]
    end

    subgraph AdminData["Admin Domain"]
        users[(users)]
        settings_c[(settings)]
        activity_logs_c[(activity_logs)]
        email_templates_c[(email_templates)]
    end

    orders --> customers
    orders --> work_orders
    orders --> invoices_c
    products_v2_c --> product_templates_c
    products_v2_c --> option_sets_c
    purchase_orders_c --> vendor_bills_c
\`\`\`

| Collection | Key Fields | Description |
|-----------|-----------|-------------|
| \`users\` | email, role, password_hash | Admin user accounts |
| \`customers\` | name, email, company, tax_exempt | Customer records |
| \`orders\` | order_id, customer_id, total, status | Order management |
| \`work_orders\` | order_id, tasks, department, priority | Production work orders |
| \`product_templates\` | name, attributes, options_config | V2 product blueprints |
| \`option_sets\` | name, choices, pricing_impact | Reusable option configurations |
| \`products_v2\` | template_id, name, price, options | V2 product instances |
| \`invoices\` | invoice_number, order_id, amount_due | AR invoice tracking |
| \`payments\` | order_id, amount, method, status | Payment records |
| \`purchase_orders\` | po_number, vendor_id, total | Vendor purchase orders |
| \`vendor_bills\` | bill_number, po_id, amount | Vendor bills |
| \`vendors\` | name, contact, credentials | Vendor records |
| \`activity_logs\` | user, action, entity, timestamp | Audit trail |
| \`settings\` | company info, tax config, email settings | System configuration |
| \`email_templates\` | name, subject, body, variables | Email templates |

---

## Services Layer

| Service | File | Responsibility |
|---------|------|---------------|
| **Pricing Service V2** | \`pricing_service_v2.py\` | Unified pricing: per_sqft, quantity_tiers, base_plus_options |
| **Email Template Service** | \`email_template_service.py\` | Template rendering and email dispatch via Resend |
| **Activity Logger** | \`activity_logger.py\` | Audit trail for all data mutations |

---

## Infrastructure

| Component | Detail |
|-----------|--------|
| **Hosting** | Kubernetes cluster (containerized) |
| **Process Manager** | Supervisor (manages frontend + backend) |
| **Frontend Port** | 3000 (internal) |
| **Backend Port** | 8001 (internal) |
| **Reverse Proxy** | Kubernetes Ingress (\`/api/*\` routes to backend) |
| **Hot Reload** | Enabled for both frontend and backend |
| **File Storage** | Local \`/app/uploads/\` (artwork, catalog, customer docs) |

### Environment Variables

| Variable | Service | Purpose |
|----------|---------|---------|
| \`REACT_APP_BACKEND_URL\` | Frontend | API base URL |
| \`MONGO_URL\` | Backend | MongoDB connection string |
| \`DB_NAME\` | Backend | Database name |
| \`JWT_SECRET\` | Backend | Token signing key |
| \`RESEND_API_KEY\` | Backend | Email service credentials |
| \`SENDER_EMAIL\` | Backend | Outbound email address |
| \`CORS_ORIGINS\` | Backend | Allowed origins |
| \`COMPANY_*\` | Backend | Company branding info |

---

## Authentication & Security

\`\`\`mermaid
flowchart LR
    Login[Login Request] --> Validate[Validate Credentials]
    Validate --> Hash[bcrypt Verify]
    Hash --> JWT[Generate JWT]
    JWT --> Cookie["Set HttpOnly Cookie (session_token)"]
    Cookie --> Client[Client Receives Cookie]
    Client --> Request[Subsequent API Requests]
    Request --> Middleware[Auth Middleware]
    Middleware --> Decode[Decode JWT via python-jose]
    Decode --> Access[Grant Access]
\`\`\`

| Feature | Implementation |
|---------|---------------|
| **Password Hashing** | bcrypt (passlib) |
| **Session Tokens** | JWT in HttpOnly cookies (\`samesite=none\`) |
| **Token Library** | python-jose |
| **MFA** | TOTP via pyotp + QR code generation |
| **Role-Based Access** | Admin, Manager, Staff roles with route guards |

---

## Testing & Quality

| Tool | Purpose |
|------|---------|
| **pytest** | Backend unit and integration tests |
| **Playwright** | End-to-end browser automation |
| **ESLint** | JavaScript/JSX linting |
| **Ruff** | Python linting |
| **Test Reports** | JSON reports in \`/app/test_reports/\` |

---

## V2 Product Architecture

\`\`\`mermaid
flowchart TB
    Template["Product Template"] --> |"defines pricing model"| PricingType{"Pricing Type"}
    PricingType --> PSF["Per Square Foot (Large Format)"]
    PricingType --> QT["Quantity Tiers (Small Format)"]
    PricingType --> BPO["Base + Options (Awards)"]
    
    Template --> |"links to"| OptionSets["Option Sets"]
    OptionSets --> Choices["Choices (with price impact)"]
    
    Template --> |"creates"| Product["Product V2"]
    Product --> |"inherits"| Pricing["Pricing Config"]
    Product --> |"inherits"| Options["Available Options"]

    Pricing --> |"calculated by"| PricingService["Pricing Service V2"]
    PricingService --> |"supports"| Compound["Compound Markup: % + Fixed"]
\`\`\`

**Pricing Models:**
- **Per Square Foot:** Width x Height x Rate (Large Format products)
- **Quantity Tiers:** Tiered pricing based on order quantity (Small Format)
- **Base + Options:** Base price + selected option surcharges (Awards)
- **Compound Markup:** Supports both percentage and fixed-amount markups

---

## Third-Party Integrations

| Service | Package | Purpose | Status |
|---------|---------|---------|--------|
| **Resend** | \`resend\` | Transactional email (order confirmations, invoices) | Active |
| **4over** | Custom scraper | Vendor product/price sync | Active |
| **Stripe** | — | Payment processing | Planned |

---

## Project Structure

\`\`\`
/app
  /backend
    /models          — Pydantic data models
    /routes          — 40+ FastAPI route modules
    /services        — Business logic layer
    /scrapers        — Vendor catalog scrapers
    /utils           — Shared utilities (activity logger, etc.)
    /uploads         — File storage
    /tests           — Backend test suites
    server.py        — FastAPI app entry point
    requirements.txt — Python dependencies

  /frontend
    /src
      /components/ui — Shadcn UI component library
      /pages         — Page components (Admin, Storefront)
      /data/docs     — Documentation content modules
      /lib           — Utility functions
    package.json     — Node dependencies
    tailwind.config  — Tailwind CSS configuration

  /memory            — PRD and project documentation
  /test_reports      — Automated test result history
\`\`\`
`
};
